import React, { useState, useRef, useEffect } from "react";
import { Checkbox } from "primereact/checkbox";
import { InputSwitch } from "primereact/inputswitch";
import {
  createRole,
  updateRole,
  getMenuAll,
  getMenuByRoleId,
} from "../../Services/RoleService";
import { useParams, useNavigate } from "react-router-dom";
import MTable from "../../Components/MTable/MTable";
import { useForm, Controller } from "react-hook-form";
import Swal from "sweetalert2";
import { handlerFormError, uuid } from "../../Utils/Utils";
import Overlay from "../../Components/Overlay/Overlay";
import { useSelector } from "react-redux";

const privileges = ["can_view", "can_create", "can_update", "can_delete"];
let procesingId = -1;

const RoleDetailPage = () => {
  const navigate = useNavigate();
  const mTable = useRef();
  const { userInfo } = useSelector((state) => state.auth);
  const { pageState, role_id } = useParams();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = useForm({ defaultValues: { is_active: true } });
  const [state, setState] = useState({ role: {}, processing: false });

  useEffect(() => {
    switch (pageState) {
      case "add":
        getMenuAll({ perpage: 100 }).then(({ data }) => {
          const menu_access = (data.data || []).map((e) => {
            return {
              menu_id: e._id,
              can_view: false,
              can_create: false,
              can_update: false,
              can_delete: false,
            };
          });
          setState({ ...state, role: { menu_access } });
        });
        break;
      case "edit":
        let requests = [getMenuAll({ perpage: 100 }), getMenuByRoleId(role_id)];
        Promise.all(requests).then(([menu, role]) => {
          const menu_access = (menu.data.data || []).map((e) => {
            const roleMenu =
              (role.data?.menu_access || []).find((r) => r.menu_id == e._id) ||
              {};
            return {
              menu_id: e._id,
              can_view: roleMenu.can_view,
              can_create: roleMenu.can_create,
              can_update: roleMenu.can_update,
              can_delete: roleMenu.can_delete,
            };
          });
          setState({ ...state, role: { ...role.data, menu_access } });
          reset(role.data);
        });
        break;
      default:
        onGoBack();
    }
  }, []);

  const isEnable = (menu, privilege) => {
    const roleMenu =
      (role.menu_access || []).find((e) => e.menu_id == menu._id) || undefined;
    if (roleMenu) {
      return roleMenu[`can_${privilege}`];
    }
    return false;
  };

  const onCheckChange =
    (menu, privilege) =>
    ({ checked }) => {
      const menu_access = [...role.menu_access];
      let roleMenu =
        (menu_access || []).find((e) => e.menu_id == menu._id) || {};
      if (!roleMenu) {
        let newMenu = {
          menu_id: menu._id,
          can_view: false,
          can_create: false,
          can_update: false,
          can_delete: false,
        };
        newMenu[`can_${privilege}`] = checked;
        menu_access.push(newMenu);
      } else {
        roleMenu[`can_${privilege}`] = checked;
      }

      const _role = { ...role, menu_access };
      setState({ ...state, role: _role });
    };

  const isEnableAll = (menu) => {
    const roleMenu =
      (role.menu_access || []).find((e) => e.menu_id == menu._id) || {};
    return privileges.every((e) => roleMenu[e]);
  };

  const onChangeAll =
    (menu) =>
    ({ checked }) => {
      const menu_access = [...role.menu_access];
      const roleMenu =
        (menu_access || []).find((e) => e.menu_id == menu._id) || {};
      privileges.forEach((e) => (roleMenu[e] = checked));
      const _role = { ...role, menu_access };
      setState({ ...state, role: _role });
    };

  const columns = [
    { id: uuid(), title: "Menu", field: "description", sortable: true },
    {
      id: uuid(),
      title: "All",
      render: (data) => {
        return (
          <Checkbox checked={isEnableAll(data)} onChange={onChangeAll(data)} />
        );
      },
    },
    {
      id: uuid(),
      title: "View",
      style: { textAlign: "center" },
      render: (data) => {
        return (
          <Checkbox
            checked={isEnable(data, "view")}
            onChange={onCheckChange(data, "view")}
          />
        );
      },
    },
    {
      id: uuid(),
      title: "Create",
      style: { textAlign: "center" },
      render: (data) => {
        return (
          <Checkbox
            checked={isEnable(data, "create")}
            onChange={onCheckChange(data, "create")}
          />
        );
      },
    },
    {
      id: uuid(),
      title: "Update",
      style: { textAlign: "center" },
      render: (data) => {
        return (
          <Checkbox
            checked={isEnable(data, "update")}
            onChange={onCheckChange(data, "update")}
          />
        );
      },
    },
    {
      id: uuid(),
      title: "Delete",
      style: { textAlign: "center" },
      render: (data) => {
        return (
          <Checkbox
            checked={isEnable(data, "delete")}
            onChange={onCheckChange(data, "delete")}
          />
        );
      },
    },
  ];

  const propsTable = {
    columns,
    getData: getMenuAll,
    showIndex: true,
    enableFilter: false,
    orders: "description:asc",
  };

  const onGoBack = () => {
    navigate(-1);
  };

  const startProcessing = () => {
    procesingId = setTimeout(() => {
      setState({ ...state, processing: true });
    }, 150);
  };

  const stopProcessing = () => {
    clearInterval(procesingId);
    setState({ ...state, processing: false });
  };

  const onSubmit = (data) => {
    const { role_name, description, is_active } = data;
    const payload = {
      role_name,
      description,
      is_active,
      menu_access: role.menu_access,
    };
    const response =
      pageState == "edit" ? updateRole(role_id, payload) : createRole(payload);
    startProcessing();
    response
      .then((res) => {
        Swal.fire({
          icon: "success",
          title: "Save data success",
          text: "Data has been saved!",
        }).then((r) => {
          onGoBack();
        });
      })
      .catch(({ response: { data } }) => {
        handlerFormError(data);
      })
      .finally((_) => stopProcessing());
  };

  const { role, processing } = state;

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">Role &amp; Access</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <a href="#">Home</a>
                </li>
                <li className="breadcrumb-item active">Role &amp; Access</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <section className="content">
        <div className="container-fluid">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="card">
              <Overlay display={processing} />
              <div className="card-header">
                <div className="card-title">
                  <i className="fa fa-address-card" />{" "}
                  {pageState &&
                    pageState.charAt(0).toUpperCase() + pageState.slice(1)}{" "}
                  Role
                </div>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="role_name">Role Name</label>
                      <input
                        id="role_name"
                        {...register("role_name", {
                          required: "Name is required",
                        })}
                        className="form-control"
                        placeholder="Role name"
                      />
                      {errors.role_name && (
                        <span className="text-danger">
                          {errors.role_name.message}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="description">Description</label>
                      <textarea
                        id="description"
                        {...register("description", {
                          required: "Name is required",
                        })}
                        className="form-control"
                        rows="3"
                        placeholder="Description"
                        style={{ resize: "none" }}
                      />
                      {errors.description && (
                        <span className="text-danger">
                          {errors.description.message}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="active">Active</label>
                      <div>
                        <Controller
                          name="is_active"
                          control={control}
                          render={({ field }) => {
                            return (
                              <InputSwitch
                                {...field}
                                checked={field.value}
                                disabled={userInfo.role_id == role_id}
                              />
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="dropdown-divider" />

                <div className="row">
                  <div className="col-md-12">
                    <MTable ref={mTable} {...propsTable} />
                  </div>
                </div>
              </div>
              <div className="card-footer">
                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    onClick={onGoBack}
                    className="btn btn-warning"
                    style={{ width: 100, marginRight: 20 }}
                  >
                    <i className="fa fa-reply" /> Back
                  </button>
                  <button
                    type="submit"
                    className="btn btn-dark"
                    style={{ width: 100 }}
                  >
                    <i className="fa fa-save" /> Save
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default RoleDetailPage;
